'use client'

import { useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { useTranslate } from '@marketplace-web/shared/i18n'
import { useDebounce, UserSelect } from '@marketplace-web/shared/ui-helpers'
import { renderValidation, useFormValidationMessage } from '@marketplace-web/shared/react-hook-form'
import { useIsMounted } from '@marketplace-web/shared/ui-state'
import { getRecentUsers } from 'data/api/get-recent-users'
import {
  transformUserSuggestions,
  UserSuggestionModel,
} from '@marketplace-web/domain/search-bar-data'

import { Field } from './types'

const DEBOUNCE_DELAY = 350

type Props = {
  isMemberRequired?: boolean
}

const MemberSelect = ({ isMemberRequired }: Props) => {
  const translate = useTranslate('help_contact_form.member')
  const isMounted = useIsMounted()

  const [recentUsers, setRecentUsers] = useState<Array<UserSuggestionModel>>([])
  const [query, setQuery] = useState('')
  const [selectedValue, setSelectedValue] = useState<UserSuggestionModel | null>(null)

  const {
    setValue,
    control,
    clearErrors,
    formState: { errors },
  } = useFormContext()

  const validationMessage = useFormValidationMessage(errors, 'help_contact_form')

  const debouncedFunction = useDebounce((value: string) => {
    if (!isMounted()) return

    setQuery(value)
  }, DEBOUNCE_DELAY)

  const handleMemberSearch = ({ target }) => {
    debouncedFunction(target.value)
  }

  useEffect(() => {
    async function fetchRecentUsers() {
      const response = await getRecentUsers(query)

      if ('errors' in response) return

      setRecentUsers(transformUserSuggestions(response.users))
    }

    fetchRecentUsers()
  }, [query])

  function handleSave(user: UserSuggestionModel) {
    if (!user) return

    clearErrors(Field.RelatedMember)
    setSelectedValue(user)
    setValue(Field.RelatedMember, user.id)
  }

  return (
    <Controller
      defaultValue={null}
      name={Field.RelatedMember}
      control={control}
      rules={{ required: isMemberRequired }}
      render={() => (
        <UserSelect
          name={Field.RelatedMember}
          title={translate('title')}
          placeholder={translate('placeholder')}
          users={recentUsers}
          onChange={handleMemberSearch}
          selectedUser={selectedValue}
          onSave={handleSave}
          validation={renderValidation(
            validationMessage(Field.RelatedMember, { required: translate('error') }),
          )}
        />
      )}
    />
  )
}

export default MemberSelect
